import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useGlobalMutation, useGlobalQuery } from '@/state/query-client/hooks';
import { useToast } from '@/hooks/use-toast';
import {
  getOvertimeBalance,
  getOvertimeLedger,
  getOvertimeList,
  payoffOvertime,
} from '../services/overtime.service';
import {
  GetOvertimeLedgerParams,
  GetOvertimeParams,
  PayoffOvertimeParams,
} from '../types/overtime.types';

export const useGetOvertimeList = (params: GetOvertimeParams) => {
  return useGlobalQuery({
    queryKey: ['hr-overtime-list', params],
    queryFn: () => getOvertimeList(params),
    staleTime: 60 * 1000,
  });
};

export const useGetOvertimeBalance = (userId: string) => {
  return useGlobalQuery({
    queryKey: ['hr-overtime-balance', userId],
    queryFn: () => getOvertimeBalance(userId),
    enabled: Boolean(userId),
    staleTime: 60 * 1000,
  });
};

export const useGetOvertimeLedger = (params: GetOvertimeLedgerParams) => {
  return useGlobalQuery({
    queryKey: ['hr-overtime-ledger', params],
    queryFn: () => getOvertimeLedger(params),
    enabled: Boolean(params.userId),
    staleTime: 60 * 1000,
  });
};

export const usePayoffOvertime = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (params: PayoffOvertimeParams) => payoffOvertime(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr-overtime-list'] });
      queryClient.invalidateQueries({ queryKey: ['hr-overtime-balance'] });
      queryClient.invalidateQueries({ queryKey: ['hr-overtime-ledger'] });
      toast({ title: t('OVERTIME_PAID_OFF') });
    },
  });
};
